// utils/request.js
import md5 from "./md5/md5.js";

// 常量配置
const APP_ID = 'com.szjl.gameyeap.home';
const APP_API_SECRET_KEY = '3x7FgK9aLp2RqYbN1vZc8sT5WmHj6D4e';

// 根据环境选择基础URL
const BASE_URL = process.env.NODE_ENV === 'development'
    ? 'http://localhost:3000'
    : 'https://www.gameyeap.com';

// URL 编码处理
const superEncodeURIComponent = (str) => {
    return encodeURIComponent(str).replace(/[+!'()*]|%20/g, (c) => {
        if (c === '%20') {
            return '+';
        } else {
            return '%' + c.charCodeAt(0).toString(16).toUpperCase();
        }
    });
};

// 生成签名
const generateSign = (params, salt) => {
    if (!params || Object.keys(params).length === 0) {
        return generateMD5Url('', salt);
    }

    if (typeof params === 'string') {
        return generateMD5Url(superEncodeURIComponent(params), salt);
    }

    if (typeof params === 'object') {
        const arr = [];

        // 处理普通对象
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined) {
                arr.push(`${key}=${superEncodeURIComponent(value)}`);
            }
        });

        return generateMD5Url(arr.sort().join('&'), salt);
    }

    return '';
};

// 生成 MD5 URL
const generateMD5Url = (urlStr, salt) => {
    const signContent = `${APP_ID}${urlStr}${salt}${APP_API_SECRET_KEY}`;
    return md5(signContent).toLowerCase();
};

// 生成请求头
export const generateHeaders = (params) => {
    // 获取浏览器语言
    const language = typeof navigator !== 'undefined' ? (navigator.language || 'en') : 'en';
    // 生成随机盐值
    const salt = Math.ceil(Math.random() * 4000000000);
    // 生成 UUID（可以使用 uuid 库）
    const uuid = generateUUID();

    return {
        'Connection': 'keep-alive',
        'Content-Type': 'application/json;charset=UTF-8',
        'Accept-Language': language,
        'App-Id': APP_ID,
        'Salt': salt,
        'Sign': generateSign(params, salt),
        'Program-Language': 'JavaScript',
        'timestamp': Date.now(),
        'UUID': null,
        'X-Requested-With': 'XMLHttpRequest',
    };
};

// 生成 UUID
const generateUUID = () => {
    // 或者使用简单的实现
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

// 请求工具函数
const TIMEOUT = 8000;
const request = async ({ url, method = 'get', params = {}, propController = null, timeout = TIMEOUT }) => {
    const headers = generateHeaders(params);

    const controller = propController ? propController : new AbortController();
    const { signal } = controller;

    const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => {
            controller.abort();
            reject(new Error(propController ? '手动取消' : '请求超时'));
        }, timeout)
    );

    const queryString = Object.entries(params)
        .filter(([_, value]) => value !== undefined && value !== null && value !== '')
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join('&');
        
    // 确保 URL 是完整的
    const fullUrl = method === 'get' ? `${BASE_URL}${url}${queryString ? `?${queryString}` : ''}` : `${BASE_URL}${url}`;
    const fetchPromise = fetch(fullUrl, {
        method,
        headers,
        signal,
        body: method !== 'get' ? JSON.stringify(params) : undefined,
    });

    try {
        const response = await Promise.race([fetchPromise, timeoutPromise]);

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.code === 0) {
            return data;
        } else {
            throw new Error(data.message || 'Request failed');
        }
    } catch (error) {
        console.error('Request error:', error);
        throw error;
    }
};

export default request;

// 封装 GET 请求
export const httpGet = async (url, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const fullUrl = `${url}?${queryString}`;
    return request('GET', fullUrl, params);
};

// 封装 POST 请求
export const httpPost = async (url, params = {}) => {
    return request('POST', url, params);
};